import type { IDE, IDEConfig } from '../types/context.types.js';

/**
 * IDE configurations for context scanning
 */
export const IDE_CONFIGS: Record<IDE, IDEConfig> = {
    cursor: {
        id: 'cursor',
        name: 'Cursor',
        icon: '🔮',
        basePath: '.cursor',
        contentPaths: {
            rules: 'rules',
            workflows: 'commands',      // Cursor uses "commands" for workflows
        },
        fileExtensions: {
            rules: ['.mdc'],            // Cursor-specific markdown
            workflows: ['.md'],
        },
        frontmatterSchema: {
            triggerField: 'globs',
            alwaysApplyField: 'alwaysApply',
            descriptionField: 'description',
        },
    },
    windsurf: {
        id: 'windsurf',
        name: 'Windsurf',
        icon: '🏄',
        basePath: '.windsurf',
        contentPaths: {
            rules: 'rules',
            workflows: 'workflows',
        },
        fileExtensions: {
            rules: ['.md'],
            workflows: ['.md'],
        },
        frontmatterSchema: {
            triggerField: 'trigger',
            alwaysApplyField: 'trigger',  // Value: 'always'
            descriptionField: 'description',
        },
    },
    antigravity: {
        id: 'antigravity',
        name: 'Antigravity',
        icon: '🚀',
        basePath: '.agent',
        contentPaths: {
            rules: 'rules',
            workflows: 'workflows',
        },
        fileExtensions: {
            rules: ['.md'],
            workflows: ['.md'],
        },
        frontmatterSchema: {
            triggerField: 'trigger',
            alwaysApplyField: 'trigger',  // Value: 'always_on'
            descriptionField: 'description',
        },
    },
    claude: {
        id: 'claude',
        name: 'Claude Code',
        icon: '🤖',
        basePath: '.claude',
        contentPaths: {
            rules: 'rules',
            workflows: 'commands',      // Claude Code uses "commands" for workflows
        },
        fileExtensions: {
            rules: ['.md'],
            workflows: ['.md'],
        },
        frontmatterSchema: {
            triggerField: 'paths',
            descriptionField: 'description',
        },
    },
    opencode: {
        id: 'opencode',
        name: 'OpenCode',
        icon: '💻',
        basePath: '.',                  // AGENTS.md lives at project root
        contentPaths: {
            rules: '.',                 // Single file: AGENTS.md
            workflows: '.opencode/command',
        },
        fileExtensions: {
            rules: ['.md'],             // Only AGENTS.md
            workflows: ['.md'],
        },
        frontmatterSchema: {
            triggerField: 'trigger',
            alwaysApplyField: 'trigger',
            descriptionField: 'description',
        },
    },
    codex: {
        id: 'codex',
        name: 'Codex',
        icon: '🧠',
        basePath: '.',
        contentPaths: {
            rules: '.',
            workflows: '.agents/skills',
            skills: '.agents/skills',
            context: '.codex',
        },
        fileExtensions: {
            rules: ['.md'],
            workflows: ['SKILL.md'],
            skills: ['SKILL.md'],
            context: ['.toml'],
        },
        frontmatterSchema: {
            triggerField: 'trigger',
            descriptionField: 'description',
        },
    },
    jai1: {
        id: 'jai1',
        name: 'Jai1 Framework',
        icon: '🤖',
        basePath: '.jai1',              // Or packages/jai1-framework
        contentPaths: {
            rules: 'rules',
            workflows: 'workflows',
            skills: 'skills',           // Folders with SKILL.md
            agents: 'agents',
            prompts: 'prompts',
            context: 'context',
        },
        fileExtensions: {
            rules: ['.md'],
            workflows: ['.md'],
            skills: ['SKILL.md'],       // Special handling for skill folders
            agents: ['.md'],
            prompts: ['.md'],
            context: ['.md'],
        },
        frontmatterSchema: {
            triggerField: 'globs',
            alwaysApplyField: 'alwaysApply',
            descriptionField: 'description',
        },
    },
};

/**
 * Get IDE config by ID
 */
export function getIDEConfig(ide: IDE): IDEConfig {
    return IDE_CONFIGS[ide];
}

/**
 * Get all IDE IDs
 */
export function getAllIDEs(): IDE[] {
    return Object.keys(IDE_CONFIGS) as IDE[];
}
